import { nextTick } from 'vue'

import { defineNuxtPlugin, useNuxtApp } from 'nuxt/app'

// Configuration for allowing iframes and specific attributes
const allowIframeAndAttributesConfig = {
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'scrolling', 'overlay', 'tooltip'],
  ADD_TAGS: ['iframe'],
  ALLOWED_ATTR: [
    'href',
    'name',
    'target',
    'title',
    'alt',
    'class',
    'id',
    'style',
    'src',
    'width',
    'height',
    'allow',
    'allowfullscreen',
    'scrolling',
    'frameborder',
    'overlay',
    'tooltip',
    'controls',
    'type',
    'data-*',
  ],
  ALLOWED_TAGS: [
    'a',
    'abbr',
    'acronym',
    'address',
    'article',
    'aside',
    'b',
    'bdi',
    'bdo',
    'big',
    'blockquote',
    'br',
    'button',
    'canvas',
    'caption',
    'center',
    'cite',
    'code',
    'col',
    'colgroup',
    'data',
    'datalist',
    'dd',
    'del',
    'details',
    'dfn',
    'dialog',
    'dir',
    'div',
    'dl',
    'dt',
    'em',
    'fieldset',
    'figcaption',
    'figure',
    'font',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'hgroup',
    'hr',
    'i',
    'img',
    'input',
    'ins',
    'kbd',
    'label',
    'legend',
    'li',
    'main',
    'map',
    'mark',
    'menu',
    'menuitem',
    'meter',
    'nav',
    'ol',
    'optgroup',
    'option',
    'output',
    'p',
    'picture',
    'pre',
    'progress',
    'q',
    'rp',
    'rt',
    'ruby',
    's',
    'samp',
    'section',
    'select',
    'small',
    'source',
    'span',
    'strong',
    'sub',
    'summary',
    'sup',
    'table',
    'tbody',
    'td',
    'textarea',
    'tfoot',
    'th',
    'thead',
    'time',
    'tr',
    'u',
    'ul',
    'var',
    'video',
    'audio',
    'wbr',
    'iframe',
  ],
}

export default defineNuxtPlugin(() => {
  const app = useNuxtApp().vueApp

  // DOMPurify requires a DOM; avoid importing it on the server.
  // We'll lazy-load it on the client when needed.
  const getDOMPurify = async () => {
    const mod = await import('dompurify')
    return mod.default
  }

  // Register the sanitized-html directive for both SSR and client
  app.directive('sanitized-html', {
    // SSR support - pass HTML through without sanitization (DOMPurify requires DOM)
    getSSRProps(binding: { value: string | { html: string; afterSanity?: (el: HTMLElement) => void } }) {
      let html: string

      if (typeof binding.value === 'object') {
        html = binding.value.html
      } else {
        html = binding.value
      }

      // NOTE: We can't use DOMPurify on the server as it requires a DOM environment
      // The HTML will be sanitized on the client side during hydration
      // To avoid hydration mismatches, ensure the content is already safe or
      // sanitize it before passing to the directive
      return {
        innerHTML: html || '',
      }
    },

    // Client-side hooks
    mounted(el: HTMLElement, binding: { value: string | { html: string; afterSanity?: (el: HTMLElement) => void } }) {
      // Only run on client side
      if (process.client) {
        let html: string
        let afterSanity: ((el: HTMLElement) => void) | undefined

        if (typeof binding.value === 'object') {
          html = binding.value.html
          afterSanity = binding.value.afterSanity
        } else {
          html = binding.value
        }

        if (html) {
          ;(async () => {
            try {
              const DOMPurify = await getDOMPurify()
              const sanitizedHtml = DOMPurify.sanitize(html, {
                ...allowIframeAndAttributesConfig,
              })
              el.innerHTML = sanitizedHtml

              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            } catch {
              // Fail-safe: if DOMPurify fails to load, fall back to raw HTML.
              // This avoids blank content and prevents runtime crashes.
              el.innerHTML = html
              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            }
          })()
        }
      }
    },
    updated(el: HTMLElement, binding: { value: string | { html: string; afterSanity?: (el: HTMLElement) => void } }) {
      // Only run on client side
      if (process.client) {
        let html: string
        let afterSanity: ((el: HTMLElement) => void) | undefined

        if (typeof binding.value === 'object') {
          html = binding.value.html
          afterSanity = binding.value.afterSanity
        } else {
          html = binding.value
        }

        if (html) {
          ;(async () => {
            try {
              const DOMPurify = await getDOMPurify()
              const sanitizedHtml = DOMPurify.sanitize(html, {
                ...allowIframeAndAttributesConfig,
              })
              el.innerHTML = sanitizedHtml

              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            } catch {
              // Fail-safe: if DOMPurify fails to load, fall back to raw HTML.
              el.innerHTML = html
              if (afterSanity && typeof afterSanity === 'function') {
                await nextTick()
                afterSanity(el)
              }
            }
          })()
        }
      }
    },
  })
})
